<template>
  <div>
    <div :class="$style.weekdayRow">
      <DailyAvailabilityModal>
        <CalendarDailyAvailabilityModal
          :selectedDayOfWeek="selectedDayOfWeek"
          :availabilities="model"
          @save="handleSaveAvailabilities"
          @cancel="closeDailyAvailabilityModal"
        ></CalendarDailyAvailabilityModal>
      </DailyAvailabilityModal>
      <FlatButton
        v-for="([dayOfWeek, dayName], index) in weekdayNames"
        :key="index"
        :class="$style.dayColumn"
        variant="base"
        @click="openDaySettings(dayOfWeek)"
      >
        <div :class="$style.dayNameWrapper">
          <span :class="$style.dayName">{{ dayName }}</span>
        </div>
        <div :class="$style.timeSlotWrapper">
          <template v-if="hasTimeslotsForDay(dayOfWeek)">
            <div
              v-for="(slot, slotIndex) in getTimeslotsForDay(dayOfWeek).slice(
                0,
                displayLimitForTimeSlots
              )"
              :key="slotIndex"
              :class="$style.timeSlot"
            >
              {{ formatTimeRange(slot.from, slot.until) }}
            </div>
            <div
              v-if="
                getTimeslotsForDay(dayOfWeek).length > displayLimitForTimeSlots
              "
              :class="$style.slotCount"
            >
              +
              {{
                getTimeslotsForDay(dayOfWeek).length - displayLimitForTimeSlots
              }}件
            </div>
          </template>
          <div v-else :class="$style.noTimeSlot">未設定</div>
        </div>
      </FlatButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useModal } from '@/hooks/useModal'

import {
  DayOfWeek,
  type DayOfWeek as DayOfWeekType,
  type CalendarDailyAvailabilityInput,
  type CalendarTimeOfDayInput,
} from '@/types/schema.generated.ts'

import { weekdayNames } from './calendar.ts'
import CalendarDailyAvailabilityModal from './CalendarDailyAvailabilityModal.vue'

const model = defineModel<CalendarDailyAvailabilityInput[]>({ required: true })

const selectedDayOfWeek = ref<DayOfWeekType>(DayOfWeek.Monday)

const {
  openDailyAvailabilityModal,
  closeDailyAvailabilityModal,
  DailyAvailabilityModal,
} = useModal('dailyAvailability')

type DailyAvailabilityType = {
  from: CalendarTimeOfDayInput
  until: CalendarTimeOfDayInput
}
type DOWToDailyAvailabilityType = Record<DayOfWeek, DailyAvailabilityType[]>

const weekdayToDailyAvailability = computed<DOWToDailyAvailabilityType>(() => {
  return model.value.reduce((acc, { dayOfWeek, from, until }) => {
    return {
      ...acc,
      [dayOfWeek]: [...(acc[dayOfWeek] || []), { from, until }],
    }
  }, {} as DOWToDailyAvailabilityType)
})

const hasTimeslotsForDay = (dow: DayOfWeekType) => {
  return !!weekdayToDailyAvailability.value[dow]?.length
}

const getTimeslotsForDay = (dow: DayOfWeekType) => {
  return weekdayToDailyAvailability.value[dow] || []
}

const displayLimitForTimeSlots = 2

const formatTimeRange = (
  from: CalendarTimeOfDayInput,
  until: CalendarTimeOfDayInput
) => {
  // HH:MM
  const padZero = (num: number): string => num.toString().padStart(2, '0')
  const fromStr = `${padZero(from.hours)}:${padZero(from.minutes)}`
  const untilStr = `${padZero(until.hours)}:${padZero(until.minutes)}`
  return `${fromStr} - ${untilStr}`
}

const openDaySettings = (dow: DayOfWeekType) => {
  selectedDayOfWeek.value = dow
  openDailyAvailabilityModal()
}

const handleSaveAvailabilities = (data: {
  timeSlots: CalendarDailyAvailabilityInput[]
  selectedDayOfWeeks: DayOfWeekType[]
}) => {
  // Step 1: Filter out slots for the selected day
  const filteredForCurrentDay = model.value.filter(
    slot => slot.dayOfWeek !== selectedDayOfWeek.value
  )

  // Step 2: Create slots for selected days
  const newSlotsForSelectedDays = data.selectedDayOfWeeks.flatMap(dow =>
    data.timeSlots.map(slot => ({
      ...slot,
      dayOfWeek: dow,
    }))
  )

  // Step 3: Filter out slots for all selected days
  const filteredForAllDays = filteredForCurrentDay.filter(
    slot => !data.selectedDayOfWeeks.includes(slot.dayOfWeek)
  )

  // Step 4: Combine all slots with new time slots
  const finalModel = [
    ...filteredForAllDays,
    ...data.timeSlots,
    ...newSlotsForSelectedDays,
  ]

  // Update the model with the new array
  model.value = finalModel

  closeDailyAvailabilityModal()
}
</script>

<style lang="scss" module>
.weekdayRow {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
  padding: 4px;
  overflow-x: auto;
  gap: 1px;
}

.dayColumn {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 85px; /* 時間表示テキスト程度の最小幅 */
  border: 1px solid $color-border-low-emphasis;
  border-radius: 8px;
  background-color: $color-surface-primary;
  height: auto; /* 自動調整 */
  min-height: 80px; /* 最小高さ */
  padding: 0; /* パディングを削除して、内容に合わせる */

  &:hover {
    background-color: $color-surface-secondary;
  }
}

.dayNameWrapper {
  width: 100%;
  border-bottom: 1px solid $color-border-low-emphasis;
  padding-top: 8px;
  padding-bottom: 8px;
}

.dayName {
  font-weight: bold;
  font-size: 14px;
  color: $color-text-and-object-high-emphasis;
  display: block;
  text-align: center;
  line-height: 1.4;
  padding-top: 4px;
}

.timeSlotWrapper {
  padding-top: 4px;
  padding-bottom: 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.timeSlot {
  font-size: 13px;
  margin: 2px 0;
  color: $color-text-and-object-medium-emphasis;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.slotCount {
  font-size: 12px;
  color: $color-text-and-object-medium-emphasis;
}

.noTimeSlot {
  font-size: 13px;
  color: $color-text-and-object-low-emphasis;
  font-style: italic;
}
</style>
